import {InaccessibleInfo} from 'auth/RequestAccess';
import DataConnector from 'connect/DataConnector';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {LegacySamplesSource} from 'notebook/sample-propagation/legacy/legacy-add-samples-modal/LegacyAddSamplesModal';
import {SampleFilters} from 'notebook/sample-propagation/SampleFilter';
import filterSample from 'notebook/sample-propagation/util/filterSample';
import {getStore} from 'redux-stores/Store';
import {HydratedFulfillment} from 'requests/fulfillments/HydratedFulfillmentConstants';

import {REQUEST_TABLE_ICON, REQUEST_TABLE_TITLE} from './RequestConstants';

interface ConnectorProps {
  requestFulfillmentsSources: Array<LegacySamplesSource>;
}

interface CollectorProps {
  entryId?: string;
  limitSamplesTo?: SampleFilters;
  children: (props: Array<LegacySamplesSource>) => JSX.Element;
}

/**
 * This DataConnector takes an entry id and retrieves all of the samples associated with
 * the request fulfillments table. The results are returned in the form of a LegacySamplesSource
 * object, so it can be easily passed to the AddSamples modal.
 *
 * Much of the code is cribbed from RequestEntryTableConnector, since both connectors are
 * designed to convert an entry's fulfillments into a rich data structure.
 */

class RequestFulfillmentsSampleSourcesConnector extends DataConnector<CollectorProps, ConnectorProps> {
  enhanceProps(): ConnectorProps {
    const {entryId} = this.props;
    if (entryId == null) {
      return {requestFulfillmentsSources: []};
    }
    const {models} = getStore().getState();
    const {fulfillmentIdsByEntryId} = models.requests.fulfillments;
    if (fulfillmentIdsByEntryId == null) {
      // FULFILLMENTS_LOADED_FOR_ENTRY is dispatched when the entry is opened
      return this.fallBackToLoading();
    }

    const hydratedFulfillmentById = this.getOrFetchMultipleMaybeInaccessibleRequired(
      ManagedModelType.HYDRATED_FULFILLMENT_BY_ID,
      fulfillmentIdsByEntryId[entryId] || []
    );
    const hydratedFulfillments: Array<HydratedFulfillment | InaccessibleInfo> =
      Object.values(hydratedFulfillmentById);
    const sampleIds: Array<string> = [];

    hydratedFulfillments.forEach((details) => {
      if (details.sampleGroup) {
        for (const tag of details.sampleGroup.tags) {
          for (const sample of tag.targetSamples || []) {
            sampleIds.push(sample.id);
          }
        }
      }
    });

    const samplesById = this.getOrFetchMultipleOptionalPartial(
      ManagedModelType.HYDRATED_STAGE_RUN_SAMPLE_BY_ID,
      sampleIds
    );

    // Ensure that the user's original sample order is maintained
    let samples = sampleIds.map((id) => samplesById[id]).filter((sample) => sample != null);

    if (this.props.limitSamplesTo != null) {
      samples = samples.filter((sample) => filterSample(sample, this.props.limitSamplesTo!));
    }

    return {
      requestFulfillmentsSources:
        samples.length > 0
          ? [
              {
                name: REQUEST_TABLE_TITLE,
                icon: REQUEST_TABLE_ICON,
                sampleDetails: samples,
              },
            ]
          : [],
    };
  }
}

const RequestFulfillmentsSampleSourcesCollector = RequestFulfillmentsSampleSourcesConnector.connect(
  (props: CollectorProps & ConnectorProps) => props.children(props.requestFulfillmentsSources)
);

export default RequestFulfillmentsSampleSourcesCollector;
